/**
 * CR-8252 (R5, option A): the onboarding questionnaires sent to suppliers, as
 * Supplier Responses rows. Customers only — a supplier answers its own on the
 * supplier Questionnaires page.
 */
import { useCallback, useEffect, useState } from 'react';
import { onboardingAPI, type FilledDataset } from '../../lib/api';
import { onboardingStatusFor, rowFromFilledDataset, type ResponseRow } from './responseRows';

const PAGE_SIZE = 100;

/**
 * Loads every onboarding questionnaire the user can see, filtered like the page.
 *
 * @param filterStatus - the page's status filter ('all' or a list status)
 * @param isCustomer - only customers load them
 * @returns the rows, a loading flag and a reloader
 */
export function useOnboardingResponses(filterStatus: string, isCustomer: boolean) {
  const [rows, setRows] = useState<ResponseRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isCustomer) {
      setRows([]);
      return;
    }
    setLoading(true);
    try {
      const all: FilledDataset[] = [];
      const status = onboardingStatusFor(filterStatus);
      for (let page = 1; ; page += 1) {
        const params: Record<string, string | number> = { page_size: PAGE_SIZE, page };
        if (status) params['status'] = status;
        const res = await onboardingAPI.getFilledDatasets(params);
        const results = res.data.results || [];
        all.push(...results);
        if (all.length >= res.data.count || results.length < PAGE_SIZE) break;
      }
      setRows(all.map(rowFromFilledDataset));
    } catch (err: unknown) {
      console.error('Error loading onboarding questionnaires:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, isCustomer]);

  useEffect(() => { void load(); }, [load]);

  return { rows, loading, reload: load };
}
